// Relationship Graph Types
// Defines connections between frameworks, capabilities, and spatial metaphors

export type RelationshipType =
  | 'prerequisite'      // A must be understood before B
  | 'complement'        // A and B work well together
  | 'alternative'       // A or B can be used (similar purpose)
  | 'contains'          // A contains B as a component
  | 'extends'           // B extends/builds upon A
  | 'conflicts'         // A and B have tension (tradeoffs)
  | 'applies-to'        // A is applied in context B
  | 'derived-from';     // B is derived from A

export type SpatialLocationType =
  | 'island-region'
  | 'mountain-elevation'
  | 'mountain-face'
  | 'mof-layer';

export interface SpatialLocation {
  type: SpatialLocationType;
  id: string;
  label: string;
  description: string;
  icon: string;
  difficulty?: 'beginner' | 'intermediate' | 'advanced' | 'expert';
}

export interface FrameworkRelationship {
  fromId: string;                 // encodedId of source framework
  toId: string;                   // encodedId of target framework
  type: RelationshipType;
  strength: number;               // 0-1, how strong the relationship is
  description?: string;           // Why they're related
  bidirectional: boolean;         // If true, relationship goes both ways
}

export interface FrameworkMetadata {
  encodedId: string;
  name: string;
  category: string;
  
  // Spatial Locations
  spatialLocations: SpatialLocation[];
  
  // Related Terms
  glossaryTerms: string[];
  
  // Tags for filtering
  tags: {
    scope?: 'individual' | 'team' | 'organization' | 'ecosystem';
    context?: 'stable' | 'turbulent' | 'accelerating' | 'realigning' | 'startup';
    degapStage?: 'decide' | 'engage' | 'gather' | 'arrange' | 'prioritize';
    domain?: string[];
  };
  
  // Difficulty/Prerequisites
  difficulty: 'beginner' | 'intermediate' | 'advanced' | 'expert';
  prerequisites: string[]; // encodedIds that should be understood first
  
  // Usage context
  bestFor?: string[];      // Use cases
  avoidWhen?: string[];    // When NOT to use
}

export interface RelationshipGraph {
  nodes: Map<string, FrameworkMetadata>;
  edges: FrameworkRelationship[];
}

export interface PathSegment {
  from: string;
  to: string;
  relationship: FrameworkRelationship;
}

export interface ConnectionPath {
  start: string;
  end: string;
  path: PathSegment[];
  distance: number;
}

export interface SmartSuggestion {
  type: 'spatial' | 'prerequisite' | 'next-step' | 'related' | 'glossary';
  title: string;
  description: string;
  targetId?: string;       // Framework ID or page path
  targetPath?: string;     // Route to navigate to
  icon: string;
  priority: number;        // 1-10 for ordering
  context?: string;        // Why this is suggested
}

export interface ContextualNavigation {
  currentFramework?: FrameworkMetadata;
  userLevel: 'beginner' | 'intermediate' | 'advanced' | 'expert';
  currentElevation?: number;
  visitedRegions: string[];
  suggestions: SmartSuggestion[];
}

// Relationship strength calculator helpers
export function calculateRelationshipStrength(
  type: RelationshipType,
  sharedTags: number,
  sharedLocations: number
): number {
  const typeWeights: Record<RelationshipType, number> = {
    'prerequisite': 0.9,
    'extends': 0.8,
    'complement': 0.7,
    'contains': 0.8,
    'applies-to': 0.6,
    'derived-from': 0.7,
    'alternative': 0.5,
    'conflicts': 0.4,
  };
  
  const baseStrength = typeWeights[type];
  const tagBonus = Math.min(sharedTags * 0.1, 0.2);
  const locationBonus = Math.min(sharedLocations * 0.1, 0.2);
  
  return Math.min(baseStrength + tagBonus + locationBonus, 1.0);
}

// Graph traversal helpers
export function findShortestPath(
  graph: RelationshipGraph,
  startId: string,
  endId: string,
  maxDepth: number = 6
): ConnectionPath | null {
  // Breadth-first search for shortest path
  const queue: { nodeId: string; path: PathSegment[]; visited: Set<string> }[] = [
    { nodeId: startId, path: [], visited: new Set([startId]) }
  ];
  
  while (queue.length > 0) {
    const current = queue.shift()!;
    
    if (current.nodeId === endId) {
      return {
        start: startId,
        end: endId,
        path: current.path,
        distance: current.path.length,
      };
    }
    
    if (current.path.length >= maxDepth) {
      continue;
    }
    
    // Find all connected nodes
    const connections = graph.edges.filter(
      edge => edge.fromId === current.nodeId || (edge.bidirectional && edge.toId === current.nodeId)
    );
    
    for (const edge of connections) {
      const nextNodeId = edge.fromId === current.nodeId ? edge.toId : edge.fromId;
      
      if (!current.visited.has(nextNodeId)) {
        const newVisited = new Set(current.visited);
        newVisited.add(nextNodeId);
        
        queue.push({
          nodeId: nextNodeId,
          path: [
            ...current.path,
            {
              from: current.nodeId,
              to: nextNodeId,
              relationship: edge,
            },
          ],
          visited: newVisited,
        });
      }
    }
  }
  
  return null; // No path found
}

export function findRelatedFrameworks(
  graph: RelationshipGraph,
  frameworkId: string,
  maxResults: number = 10
): Array<{ framework: FrameworkMetadata; relationship: FrameworkRelationship }> {
  const related = graph.edges
    .filter(edge => edge.fromId === frameworkId || (edge.bidirectional && edge.toId === frameworkId))
    .map(edge => {
      const relatedId = edge.fromId === frameworkId ? edge.toId : edge.fromId;
      const framework = graph.nodes.get(relatedId);
      
      return framework
        ? { framework, relationship: edge }
        : null;
    })
    .filter(Boolean)
    .sort((a, b) => b!.relationship.strength - a!.relationship.strength)
    .slice(0, maxResults);
  
  return related as Array<{ framework: FrameworkMetadata; relationship: FrameworkRelationship }>;
}

export function getPrerequisites(
  graph: RelationshipGraph,
  frameworkId: string
): FrameworkMetadata[] {
  const framework = graph.nodes.get(frameworkId);
  if (!framework) return [];
  
  return framework.prerequisites
    .map(prereqId => graph.nodes.get(prereqId))
    .filter(Boolean) as FrameworkMetadata[];
}

export function getNextSteps(
  graph: RelationshipGraph,
  frameworkId: string
): FrameworkMetadata[] {
  // Find frameworks that list this one as a prerequisite
  const nextSteps: FrameworkMetadata[] = [];
  
  graph.nodes.forEach(framework => {
    if (framework.prerequisites.includes(frameworkId)) {
      nextSteps.push(framework);
    }
  });
  
  return nextSteps;
}

